import { format, isValid, parse } from 'date-fns';
import React from 'react';
import { isMobile } from 'react-device-detect';

import { CHART_DATA_KEYS, CHART_TYPES } from '_constants/charts';
import { FILTER_TYPES } from '_constants/reports';
import { theme } from '_constants/theme';

import Tooltip from 'components/atomic/Chart/custom/Tooltip';
import ActivePoint from 'components/atomic/Chart/layers/ActivePoint';

import { formatCurrency } from './format';
import { luminance } from './theme';

const FORMAT_MASK_MAP = {
  [FILTER_TYPES.monthRange]: 'MMM, yyyy',
  [FILTER_TYPES.monthToDate]: 'MMM dd, yy',
  [FILTER_TYPES.lastMonth]: 'MMM dd, yy',
  [FILTER_TYPES.lastThreeMonth]: 'MMM, yyyy',
  [FILTER_TYPES.yearToDate]: 'MMM, yyyy',
  [FILTER_TYPES.dateRange]: 'MMM dd, yy',
};

const COLORS_MAP = {
  [CHART_DATA_KEYS.expenses]: luminance(theme.colors.red.primary, 0.4),
  [CHART_DATA_KEYS.income]: luminance(theme.colors.green.primary, 0.1),
};

const formatAxisBottomValue =
  (valuesToShow = []) =>
  (value) => {
    if (!valuesToShow.includes(value)) {
      return '';
    }

    if (value?.includes('&')) {
      const fragments = value.split('&');
      return fragments[0];
    }

    return value;
  };

const renderCustomTooltip = ({ slice }) => {
  const { points = [] } = slice || {};
  return <Tooltip points={points} />;
};

const createLineChartData = ({ data = {}, filterType }) => {
  const legendKeys = Object.keys(data);

  return legendKeys.map((key) => {
    const dateStringArr = Object.keys(data[key]);

    return {
      id: key,
      data: dateStringArr.map((dateString) => {
        if (isValid(parse(dateString, 'yyyy-MM', new Date()))) {
          return {
            x: format(parse(dateString, 'yyyy-MM', new Date()), `${FORMAT_MASK_MAP[filterType]}&MMMM, yyyy`),
            y: data[key][dateString],
          };
        }

        if (isValid(parse(dateString, 'yyyy-MM-dd', new Date()))) {
          return {
            x: format(parse(dateString, 'yyyy-MM-dd', new Date()), `${FORMAT_MASK_MAP[filterType]}&MMMM dd, yyyy`),
            y: data[key][dateString],
          };
        }

        return {
          x: dateString,
          y: data[key][dateString],
        };
      }),
    };
  });
};

const createLineChartProps = ({ data = [], filterType }) => {
  const xArr = data[0].data.map(({ x }) => x);
  const maxXArrLen = xArr?.length;
  let divider = Math.ceil(maxXArrLen / (isMobile ? 4 : 8));

  if (
    [FILTER_TYPES.dateRange, FILTER_TYPES.monthToDate, FILTER_TYPES.lastMonth, FILTER_TYPES.lastThreeMonth].includes(
      filterType
    )
  ) {
    divider = Math.ceil(maxXArrLen / (isMobile ? 6 : 10));
  }

  const narrowedDateStringArr = xArr.filter((_, i) => i % divider === 0);

  return {
    data,
    animate: true,
    curve: 'linear',
    colors: data.map(({ id }) => id).map((key) => COLORS_MAP[key]),
    defs: [
      {
        colors: [
          {
            color: COLORS_MAP[data[0]?.id],
            offset: 0,
          },
          {
            color: COLORS_MAP[data[0]?.id],
            offset: 100,
            opacity: 0,
          },
        ],
        id: 'gradientA',
        type: 'linearGradient',
      },
    ],
    enableGridY: false,
    enableGridX: false,
    enablePoints: narrowedDateStringArr.length === 1 ? true : false,
    pointSize: 10,
    enableArea: data?.length > 1 ? false : true,
    enableSlices: 'x',
    fill: [
      {
        id: 'gradientA',
        match: '*',
      },
    ],
    yScale: {
      stacked: data?.length > 1 ? false : true,
      type: 'linear',
    },
    yFormat: formatCurrency({ maximumFractionDigits: 0 }),
    axisLeft: {
      format: formatCurrency({ maximumFractionDigits: 0 }),
    },
    axisBottom: {
      format: formatAxisBottomValue(narrowedDateStringArr),
    },
    margin: {
      bottom: 30,
      left: isMobile ? 65 : 85,
      right: isMobile ? 30 : 40,
      top: 10,
    },
    layers:
      narrowedDateStringArr.length > 1
        ? ['grid', 'axes', 'areas', 'lines', 'crosshair', 'slices', 'mesh', 'legends', ActivePoint]
        : undefined,
    sliceTooltip: renderCustomTooltip,
    theme: {
      axis: {
        domain: {
          line: {
            stroke: theme.colors.gray.secondary,
            strokeWidth: 0.2,
          },
        },
        ticks: {
          line: {
            stroke: theme.colors.gray.secondary,
            strokeWidth: 0.2,
          },
          text: {
            fill: theme.colors.gray.secondary,
            fontFamily: 'Inter',
            fontSize: `${isMobile ? theme.fontSizes.small2X : theme.fontSizes.smallX}px`,
            fontWeight: 600,
          },
        },
      },
    },
  };
};

export const createChartData = ({ type, filterType, data } = {}) => {
  const MAP = {
    [CHART_TYPES.line]: createLineChartData,
  };

  return MAP[type] ? MAP[type]({ data, filterType }) : {};
};

export const createChartProps = ({ type, filterType, data } = {}) => {
  const MAP = {
    [CHART_TYPES.line]: createLineChartProps,
  };

  return MAP[type] ? MAP[type]({ data, filterType }) : {};
};
